import React, { useState, useEffect } from 'react';
import { driverReportsApi } from '../utils/driverReportsApi';
import { formatCurrency } from '../utils/helpers';

function Fahrerabrechnung() {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadDrivers();
    
    // Set default period to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  }, []);

  const loadDrivers = async () => {
    try {
      const data = await driverReportsApi.getAllDrivers();
      setDrivers(data || []);
      
      // Auto-select first driver if available
      if (data && data.length > 0) {
        setSelectedDriver(data[0].driver_id);
      }
    } catch (err) {
      console.error('Error loading drivers:', err);
    }
  };

  const loadReport = async () => {
    if (!selectedDriver || !startDate || !endDate) {
      setMessage({ type: 'error', text: 'Bitte Fahrer und Zeitraum wählen' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);
      const data = await driverReportsApi.getReport(selectedDriver, startDate, endDate);
      setReport(data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Fahrerabrechnung
        </h1>
        <p className="text-[var(--text-secondary)] mt-1">Lieferungen & Umsätze pro Fahrer</p>
      </div>

      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-[var(--success-bg)] text-[var(--success-text)] border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Selection */}
      <div className="dark-card rounded-lg shadow border p-6 mb-6">
        <h2 className="text-lg font-bold mb-4">Abrechnung erstellen</h2>
        
        <div className="grid grid-cols-4 gap-4">
          {/* Driver */}
          <div>
            <label className="block text-sm font-medium mb-2">Fahrer</label>
            <select
              value={selectedDriver}
              onChange={(e) => setSelectedDriver(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="driver-select"
            >
              <option value="">Bitte wählen</option>
              {drivers.map((driver) => (
                <option key={driver.driver_id} value={driver.driver_id}>
                  {driver.name}
                </option>
              ))}
            </select>
          </div>

          {/* Period */}
          <div>
            <label className="block text-sm font-medium mb-2">Von</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="start-date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Bis</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="end-date"
            />
          </div>

          {/* Button */}
          <div>
            <label className="block text-sm font-medium mb-2">&nbsp;</label>
            <button
              onClick={loadReport}
              disabled={loading || !selectedDriver}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50"
              data-testid="load-report-button"
            >
              {loading ? 'Lädt...' : 'Anzeigen'}
            </button>
          </div>
        </div>
      </div>

      {/* Report */}
      {report && (
        <>
          {/* Header */}
          <div className="dark-card rounded-lg shadow border p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">{report.driver_name}</h2>
                <p className="text-sm text-[var(--text-secondary)] mt-1">
                  {new Date(report.period_start).toLocaleDateString('de-DE')} -{' '}
                  {new Date(report.period_end).toLocaleDateString('de-DE')}
                </p>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {/* Deliveries */}
            <div className="dark-card rounded-lg shadow border p-6">
              <div className="text-sm text-[var(--text-secondary)] uppercase tracking-wide">Lieferungen</div>
              <div className="text-4xl font-bold text-[var(--accent-primary)] mt-2">{report.delivered_count}</div>
              <div className="text-xs text-[var(--text-secondary)] mt-1">ausgeliefert</div>
            </div>

            {/* Revenue */}
            <div className="dark-card rounded-lg shadow border p-6">
              <div className="text-sm text-[var(--text-secondary)] uppercase tracking-wide">Umsatz</div>
              <div className="text-4xl font-bold text-green-600 mt-2">
                {formatCurrency(report.delivered_total)}
              </div>
              <div className="text-xs text-[var(--text-secondary)] mt-1">zugewiesen & ausgeliefert</div>
            </div>

            {/* Tips */}
            <div className="dark-card rounded-lg shadow border p-6">
              <div className="text-sm text-[var(--text-secondary)] uppercase tracking-wide">Trinkgeld</div>
              <div className="text-4xl font-bold text-yellow-600 mt-2">
                {formatCurrency(report.tips_total)}
              </div>
              <div className="text-xs text-[var(--text-secondary)] mt-1">
                {report.tips_cash > 0 && `Bar: ${formatCurrency(report.tips_cash)} `}
                {report.tips_card > 0 && `Karte: ${formatCurrency(report.tips_card)}`}
                {report.tips_total === 0 && 'nicht erfasst'}
              </div>
            </div>

            {/* Cancelled */}
            <div className="dark-card rounded-lg shadow border p-6">
              <div className="text-sm text-[var(--text-secondary)] uppercase tracking-wide">Abgebrochen</div>
              <div className="text-4xl font-bold text-red-600 mt-2">{report.cancelled_count}</div>
              <div className="text-xs text-[var(--text-secondary)] mt-1">Problemfälle</div>
            </div>
          </div>

          {/* Cancelled Details */}
          {report.cancelled_count > 0 && (
            <div className="dark-card rounded-lg shadow border p-6 mb-6">
              <h3 className="text-lg font-bold mb-4">Abgebrochene Lieferungen</h3>
              <div className="space-y-2">
                {report.cancelled_orders.map((order, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-red-50 rounded-lg border border-red-200 flex justify-between items-center"
                  >
                    <div>
                      <div className="font-semibold">{order.customer_name}</div>
                      <div className="text-xs text-[var(--text-secondary)]">
                        {new Date(order.cancelled_at).toLocaleString('de-DE')}
                      </div>
                    </div>
                    <div className="text-sm text-red-700">{order.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="bg-[var(--accent-muted)] rounded-lg border-2 border-blue-200 p-6">
            <div className="text-sm text-blue-800 mb-2">
              <strong>Zusammenfassung</strong>
            </div>
            <div className="text-lg">
              <strong>{report.driver_name}</strong> hat im Zeitraum{' '}
              <strong>{report.delivered_count} Lieferungen</strong> erfolgreich ausgeliefert
              {report.tips_total > 0 && (
                <>
                  {' '}
                  und <strong>{formatCurrency(report.tips_total)}</strong> Trinkgeld erhalten
                </>
              )}
              .
              {report.cancelled_count > 0 && (
                <>
                  {' '}
                  <strong>{report.cancelled_count} Lieferungen</strong> wurden abgebrochen.
                </>
              )}
            </div>
          </div>
        </>
      )}

      {!report && !loading && (
        <div className="text-center py-12 text-[var(--text-secondary)]">
          Wählen Sie einen Fahrer und Zeitraum
        </div>
      )}
    </div>
  );
}

export default Fahrerabrechnung;
